import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Activity } from "lucide-react";
import { formatLiveMinute } from "../lib/liveMinute";

/**
 * Iter 102 — Goals Wall ticker
 *
 * Public real-time feed showing every goal scored in the last 30
 * minutes across all live matches. Powered by `db.live_match_state`
 * which is updated every 60s by the Web Push cron.
 *
 * Auto-refreshes every 60s. Hidden when no live goals.
 */
const GoalsWallTicker = () => {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    const fetchFeed = async () => {
      try {
        const r = await axios.get(`${API}/goals/feed?minutes=30&limit=20`, { timeout: 15000 });
        if (!cancel) setFeed(r.data?.feed || []);
      } catch {
        if (!cancel) setFeed([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    fetchFeed();
    const id = setInterval(fetchFeed, 60_000);
    return () => {
      cancel = true;
      clearInterval(id);
    };
  }, []);

  if (loading) return null;
  if (!feed.length) return null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-rose-500/30 bg-rose-500/5 mb-8"
      data-testid="goals-wall-ticker"
    >
      <div className="flex items-stretch">
        <div className="flex items-center gap-2 px-4 py-3 bg-rose-500/15 border-r border-rose-500/30 shrink-0">
          <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
          <span className="text-[10px] font-bold tracking-[0.12em] uppercase text-rose-600">
            Goals Wall
          </span>
          <Activity className="w-3.5 h-3.5 text-rose-500" />
        </div>
        <div className="flex-1 overflow-hidden relative">
          <div
            className="flex items-center gap-6 py-3 px-4 animate-marquee whitespace-nowrap"
            style={{ animation: "marquee 60s linear infinite" }}
          >
            {[...feed, ...feed].map((g, i) => (
              <span key={`${g.fixture_id}-${i}`} className="inline-flex items-center gap-2 text-sm">
                <span className="text-amber-500">⚽</span>
                <span className="font-bold">{g.home_team}</span>
                <span className="font-display font-bold tabular-nums text-rose-500">
                  {g.score_home}–{g.score_away}
                </span>
                <span className="font-bold">{g.away_team}</span>
                <span className="text-[11px] text-muted-foreground">
                  {g.minute ? (formatLiveMinute(g.minute, g.minute_extra, g.status) || `${g.minute}'`) : g.status} · {g.league_name}
                </span>
                <span className="text-muted-foreground/40 mx-2">•</span>
              </span>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

export default GoalsWallTicker;
